import { useId, useLayoutEffect, useRef, useState } from "preact/hooks";
import type { MessageRow } from "./api";
import { messages, moveTargets, readsAll, selectedText, starsAll } from "./bulk";
import { Icon } from "./icons";

interface SelectBarProps {
  /** How many are chosen, of how many the listing has (null: not known) and whether every row of it is loaded. */
  count: number;
  total: number | null;
  complete: boolean;
  /** Select all is still loading the rest of the listing (the app ticks the rows as they come). */
  loading: boolean;
  /** Every row there is to choose is chosen: the button deselects. */
  all: boolean;
  canAll: boolean;
  onAll: () => void;
  onCancel: () => void;
}

/** The list's toolbar while messages are being chosen: Select all (every message of the listing, loading the rest
 * of it first, up to SELECT_ALL_MAX) or Deselect all, how many are chosen (of how many, while not all are loaded),
 * and Cancel, which goes back to opening messages. */
export function SelectBar({ count, total, complete, loading, all, canAll, onAll, onCancel }: SelectBarProps) {
  return (
    <div class="toolbar select-bar">
      <button class="text-button" disabled={!canAll || loading} onClick={onAll}>
        {all ? "Deselect all" : "Select all"}
      </button>
      <h2 class="title" aria-live="polite">
        {selectedText(count, total, complete, loading)}
      </h2>
      <button class="text-button select-cancel" onClick={onCancel}>
        Cancel
      </button>
    </div>
  );
}

interface Props {
  /** The messages chosen, as the list shows them. */
  rows: MessageRow[];
  folders: string[];
  onTrash: () => void;
  onMove: (folder: string) => void;
  /** Star all (true), or remove the star from all (false): which one the buttons tell. */
  onStar: (flagged: boolean) => void;
  onRead: (read: boolean) => void;
}

/** The actions on the chosen messages, at the foot of the list as in Yahoo's app. Star and Read go the way most of
 * them need: Star stars those without one (Unstar when all have it), Read marks the unread ones read (Unread when
 * none is). Move asks for the folder. */
export function BulkBar({ rows, folders, onTrash, onMove, onStar, onRead }: Props) {
  const [moving, setMoving] = useState(false);
  const none = rows.length === 0;
  const flagged = starsAll(rows);
  const read = readsAll(rows);
  return (
    <>
      <div class="bulk-bar" role="toolbar" aria-label="Actions on the selected messages">
        <button disabled={none} onClick={onTrash}>
          <Icon name="trash" />
          Delete
        </button>
        <button disabled={none} onClick={() => setMoving(true)}>
          <Icon name="move" />
          Move
        </button>
        <button disabled={none} onClick={() => onStar(flagged)}>
          <Icon name="star" filled={!flagged} />
          {flagged ? "Star" : "Unstar"}
        </button>
        <button disabled={none} title={read ? "Mark as read" : "Mark as unread"} onClick={() => onRead(read)}>
          <Icon name={read ? "read" : "unread"} />
          {read ? "Read" : "Unread"}
        </button>
      </div>
      {moving && (
        <MoveSheet
          count={rows.length}
          folders={moveTargets(folders, rows)}
          onMove={(f) => {
            setMoving(false);
            onMove(f);
          }}
          onCancel={() => setMoving(false)}
        />
      )}
    </>
  );
}

/** The folder to move the chosen messages to. A modal dialog: Escape and a tap beside it move nothing. */
function MoveSheet({ count, folders, onMove, onCancel }: { count: number; folders: string[]; onMove: (folder: string) => void; onCancel: () => void }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const id = useId();

  useLayoutEffect(() => {
    const el = dialog.current;
    if (!el || el.open) return;
    if (typeof el.showModal === "function") el.showModal();
    else el.setAttribute("open", "");
  }, []);

  return (
    <dialog
      ref={dialog}
      class="sheet move-sheet"
      aria-labelledby={`${id}-title`}
      onCancel={(e) => {
        e.preventDefault();
        onCancel();
      }}
      onClick={(e) => e.target === dialog.current && onCancel()}
    >
      <div class="sheet-body">
        <h3 id={`${id}-title`}>Move {messages(count)} to</h3>
        <div class="move-options">
          {folders.map((f) => (
            <button key={f} class="chip" onClick={() => onMove(f)}>
              {f}
            </button>
          ))}
        </div>
        <button class="text-button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </dialog>
  );
}
